import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { Redirect } from "react-router-dom";
import { FaPlus, FaToggleOn, FaToggleOff, FaTrash } from "react-icons/fa";
import SidebarPanelNavigation from "../../../../components/SidebarPanelNavigation";
import useAuth from "../../../../contexts/auth";
import {
  listWhatsAppWebSegments,
  listWhatsAppWebChannels,
  listWhatsAppWebGroups,
  listWhatsAppWebTemplates,
  listWhatsAppWebMonitoring,
  createWhatsAppWebMonitoring,
  updateWhatsAppWebMonitoring,
  deleteWhatsAppWebMonitoring,
} from "../../../../services/api";

const PLATFORMS = ["shopee", "amazon", "magalu", "mercadolivre", "natura", "aliexpress"];

export default function MonitoringPage() {
  const { user } = useAuth();
  const [segments, setSegments] = useState([]);
  const [segmentId, setSegmentId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [show, setShow] = useState(false);
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [form, setForm] = useState(null);

  // Carrega dropdowns
  useEffect(() => {
    listWhatsAppWebSegments().then((r) => setSegments(r?.data || []));
    listWhatsAppWebChannels().then((r) => setChannels(r?.data?.channels || []));
    listWhatsAppWebTemplates().then((r) => setTemplates(r?.data || []));
  }, []);

  const reload = useCallback(async () => {
    if (!segmentId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const r = await listWhatsAppWebMonitoring(segmentId);
      setItems(r?.data || []);
    } finally {
      setLoading(false);
    }
  }, [segmentId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!user) return <Redirect to="/entrar" />;
  if (user.type !== "admin") return <Redirect to="/" />;

  async function loadGroupsForChannel(chId) {
    if (!chId) return setGroups([]);
    const r = await listWhatsAppWebGroups({ channelId: chId, pageSize: 1000 });
    setGroups(r?.data || []);
  }

  function newMonitoring() {
    setForm({
      segment_id: segmentId,
      source_channel_id: "",
      source_group_jid: "",
      source_group_name: "",
      is_active: true,
      publish_target: "site",
      board: "quente",
      link_only_filter: true,
      link_conversion_enabled: true,
      link_conversion_platforms: ["shopee", "amazon"],
      keep_original_image: true,
      keep_original_text: true,
      template_id: null,
    });
    setShow(true);
  }

  async function save() {
    if (!form.source_channel_id || !form.source_group_jid) {
      alert("Selecione canal e grupo origem.");
      return;
    }
    try {
      await createWhatsAppWebMonitoring({ ...form, board: "quente" });
      setShow(false);
      reload();
    } catch (err) {
      alert("Falha ao salvar monitoramento.");
    }
  }

  async function toggle(item) {
    await updateWhatsAppWebMonitoring(item.id, { is_active: !item.is_active });
    reload();
  }

  async function remove(item) {
    if (!window.confirm("Remover monitoramento?")) return;
    await deleteWhatsAppWebMonitoring(item.id);
    reload();
  }

  return (
    <Container fluid className="p-4">
      <Row>
        <Col md={3} lg={2}>
          <SidebarPanelNavigation isAdmin />
        </Col>
        <Col md={9} lg={10}>
          <h2 className="mb-1">📡 Monitoramento de Grupo</h2>
          <p className="text-muted">
            Replica automaticamente mensagens de um grupo origem com conversão de
            link de afiliado.
          </p>

          <Alert variant="info" className="small">
            ⚠️ <strong>Anti-loop:</strong> o canal que monitora deve ser DIFERENTE
            dos canais que disparam no segmento. Caso contrário, o sistema entra em
            loop.
          </Alert>

          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col md={9}>
                  <Form.Label>Selecione o segmento de destino</Form.Label>
                  <Form.Control
                    as="select"
                    value={segmentId}
                    onChange={(e) => setSegmentId(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {segments.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={newMonitoring}
                    disabled={!segmentId}
                  >
                    <FaPlus /> Novo Monitor
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : !segmentId ? (
            <Card className="text-center p-4">
              <Card.Body className="text-muted">
                Selecione um segmento pra ver os monitoramentos.
              </Card.Body>
            </Card>
          ) : items.length === 0 ? (
            <Card className="text-center p-4">
              <Card.Body className="text-muted">
                Nenhum monitoramento configurado pra este segmento.
              </Card.Body>
            </Card>
          ) : (
            <Row>
              {items.map((m) => (
                <Col md={6} key={m.id} className="mb-3">
                  <Card>
                    <Card.Body>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h6 className="mb-0">
                            👀 {m.source_group_name || m.source_group_jid}
                          </h6>
                          <small className="text-muted">
                            via canal: {m.source_channel?.name}
                          </small>
                        </div>
                        <Badge variant={m.is_active ? "success" : "secondary"}>
                          {m.is_active ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                      <hr />
                      <small>
                        Plataformas:{" "}
                        {(m.link_conversion_platforms || []).join(", ") || "—"}
                      </small>
                      <div className="mt-2 d-flex" style={{ gap: 6 }}>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => toggle(m)}
                        >
                          {m.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => remove(m)}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Col>
      </Row>

      {/* Modal Novo Monitor */}
      <Modal show={show} onHide={() => setShow(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Configurar Monitoramento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {form && (
            <>
              <Form.Group>
                <Form.Label>Canal observador *</Form.Label>
                <Form.Control
                  as="select"
                  value={form.source_channel_id}
                  onChange={(e) => {
                    setForm({ ...form, source_channel_id: e.target.value });
                    loadGroupsForChannel(e.target.value);
                  }}
                >
                  <option value="">Selecione...</option>
                  {channels.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted">
                  Canal que ESCUTA as mensagens. Diferente dos canais que disparam.
                </Form.Text>
              </Form.Group>

              <Form.Group>
                <Form.Label>Grupo origem *</Form.Label>
                <Form.Control
                  as="select"
                  value={form.source_group_jid}
                  onChange={(e) => {
                    const g = groups.find((x) => x.whatsapp_jid === e.target.value);
                    setForm({
                      ...form,
                      source_group_jid: e.target.value,
                      source_group_name: g?.name || "",
                    });
                  }}
                  disabled={!form.source_channel_id}
                >
                  <option value="">Selecione...</option>
                  {groups.map((g) => (
                    <option key={g.whatsapp_jid} value={g.whatsapp_jid}>
                      {g.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <hr />
              <h6>🔗 Conversão de Links</h6>
              <Form.Check
                type="switch"
                id="link_conversion_enabled"
                label="Ativar conversão de link"
                checked={!!form.link_conversion_enabled}
                onChange={(e) =>
                  setForm({ ...form, link_conversion_enabled: e.target.checked })
                }
              />
              <Form.Check
                type="switch"
                id="keep_original_image"
                label="Manter imagem original"
                checked={!!form.keep_original_image}
                onChange={(e) =>
                  setForm({ ...form, keep_original_image: e.target.checked })
                }
              />
              <Form.Check
                type="switch"
                id="keep_original_text"
                label="Manter texto original (sem reescrever com IA)"
                checked={!!form.keep_original_text}
                onChange={(e) =>
                  setForm({ ...form, keep_original_text: e.target.checked })
                }
              />
              <Form.Check
                type="switch"
                id="link_only_filter"
                label="Apenas mensagens com link"
                checked={!!form.link_only_filter}
                onChange={(e) =>
                  setForm({ ...form, link_only_filter: e.target.checked })
                }
              />

              <Form.Label className="mt-3">
                Plataformas para conversão
              </Form.Label>
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {PLATFORMS.map((p) => (
                  <Form.Check
                    key={p}
                    type="checkbox"
                    label={p}
                    checked={(form.link_conversion_platforms || []).includes(p)}
                    onChange={(e) => {
                      const curr = form.link_conversion_platforms || [];
                      const next = e.target.checked
                        ? [...curr, p]
                        : curr.filter((x) => x !== p);
                      setForm({ ...form, link_conversion_platforms: next });
                    }}
                  />
                ))}
              </div>

              {!form.keep_original_text && (
                <Form.Group className="mt-3">
                  <Form.Label>Template (reescrever com IA)</Form.Label>
                  <Form.Control
                    as="select"
                    value={form.template_id || ""}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        template_id: e.target.value || null,
                      })
                    }
                  >
                    <option value="">Sem template - IA livre</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={save}>
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

